import { ArrowLeft } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { ThemePicker } from "@/components/ThemePicker";
import { VariantPicker } from "@/components/VariantPicker";
import { PrimaryColorPicker } from "@/components/PrimaryColorPicker";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTheme } from "@/hooks/useTheme";
import { useNavigate } from "react-router-dom";

export function AppearanceSettings() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { displayTheme } = useTheme();

  return (
    <div className="p-6 space-y-8">
      {isMobile && (
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/settings')}
            className="h-8 px-2 -ml-2 text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('settings')}
          </Button>
        </div>
      )}

      <div>
        <h1 className="text-xl font-semibold tracking-tight">{t('appearance')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('appearanceDescription')}
        </p>
      </div>

      <div className="space-y-6">
        {/* Theme Mode */}
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-medium">{t('themeMode')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('themeModeDescription')}
            </p>
          </div>
          <div className="max-w-xs">
            <ThemePicker />
          </div>
        </div>

        {/* Theme Variant */}
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-medium">
              {displayTheme === 'light' ? t('lightVariant') : t('darkVariant')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {displayTheme === 'light' ? t('lightVariantDescription') : t('darkVariantDescription')}
            </p>
          </div>
          <div className="max-w-xs">
            <VariantPicker />
          </div>
        </div>

        {/* Primary Color */}
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-medium">{t('primaryColor')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('primaryColorDescription')}
            </p>
          </div>
          <PrimaryColorPicker />
        </div>

        {/* Preview Section */}
        <div className="space-y-3 pt-4 border-t">
          <div>
            <h3 className="text-sm font-medium">{t('preview')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('previewDescription')}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <div className="h-10 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-xs font-medium text-primary-foreground">Primary</span>
              </div>
              <span className="text-xs text-muted-foreground">{t('primary')}</span>
            </div>
            <div className="space-y-1.5">
              <div className="h-10 rounded-lg bg-secondary flex items-center justify-center">
                <span className="text-xs font-medium text-secondary-foreground">Secondary</span>
              </div>
              <span className="text-xs text-muted-foreground">{t('secondary')}</span>
            </div>
            <div className="space-y-1.5">
              <div className="h-10 rounded-lg bg-muted flex items-center justify-center">
                <span className="text-xs font-medium text-muted-foreground">Muted</span>
              </div>
              <span className="text-xs text-muted-foreground">{t('muted')}</span>
            </div>
          </div>
          {/* Button previews */}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button size="sm">Primary Button</Button>
            <Button size="sm" variant="secondary">Secondary</Button>
            <Button size="sm" variant="outline">Outline</Button>
            <Button size="sm" variant="ghost">Ghost</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppearanceSettings;
